import { Link, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '../../shared/ui/Button';
import { apiErrorMessage } from '../../shared/api/errors';
import { formatDateTime } from '../../shared/lib/format';
import { Card, EmptyState, ErrorText, PageHeader, Spinner } from '../../shared/ui/primitives';
import { closePartnershipRequest, listPartnershipRequests } from '../partnership/api';

/** Заявки «Сотрудничество» из публичной формы, новые сверху. */
export function PartnershipRequestsPage() {
  const [params, setParams] = useSearchParams();
  const status = params.get('status') === 'closed' ? 'closed' : 'new';
  const parsedPage = Number(params.get('page') ?? '0');
  const page = Number.isSafeInteger(parsedPage) && parsedPage >= 0 ? parsedPage : 0;
  const queryClient = useQueryClient();
  const close = useMutation({
    mutationFn: closePartnershipRequest,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'partnership-requests'] }),
  });
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'partnership-requests', status, page],
    queryFn: () => listPartnershipRequests(status, page * 50),
  });

  return (
    <section className="space-y-5">
      <PageHeader title="Заявки на сотрудничество" />

      <div className="flex gap-2" aria-label="Статус заявок">
        {(['new', 'closed'] as const).map((value) => (
          <Button
            key={value}
            variant={status === value ? 'primary' : 'secondary'}
            aria-pressed={status === value}
            onClick={() => setParams({ status: value })}
          >
            {value === 'new' ? 'Новые' : 'Закрытые'}
          </Button>
        ))}
      </div>
      {close.isError && (
        <ErrorText>{apiErrorMessage(close.error, 'Не удалось закрыть заявку')}</ErrorText>
      )}
      {isLoading && <Spinner />}
      {isError && <ErrorText>{apiErrorMessage(error)}</ErrorText>}
      {data && data.items.length === 0 && (
        <EmptyState>{status === 'new' ? 'Новых заявок нет.' : 'Закрытых заявок нет.'}</EmptyState>
      )}

      {data && data.items.length > 0 && (
        <Card className="divide-y divide-line overflow-hidden">
          {data.items.map((r) => (
            <div key={r.id} className="space-y-1 px-4 py-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-medium text-ink">{r.fullName}</p>
                <span className="text-xs text-muted">{formatDateTime(r.createdAt)}</span>
              </div>
              <p className="text-sm text-muted">
                <a href={`tel:${r.phone}`} className="text-accent hover:text-accent-600">
                  {r.phone}
                </a>
                {r.email && (
                  <>
                    {' · '}
                    <a href={`mailto:${r.email}`} className="text-accent hover:text-accent-600">
                      {r.email}
                    </a>
                  </>
                )}
                {[r.clinic, r.city].filter(Boolean).map((s) => ` · ${s}`)}
              </p>
              {r.comment && <p className="whitespace-pre-line text-sm text-ink">{r.comment}</p>}
              {r.closedAt && (
                <p className="text-xs text-muted">Закрыта {formatDateTime(r.closedAt)}</p>
              )}
              <div className="flex flex-wrap items-center gap-3 pt-2 text-sm">
                {r.status === 'new' && (
                  <>
                    <Link
                      className="font-medium text-accent"
                      to={`/admin/users/new?requestId=${r.id}`}
                    >
                      Открыть и создать пользователя
                    </Link>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={close.isPending}
                      onClick={() => close.mutate(r.id)}
                    >
                      Закрыть без создания пользователя
                    </Button>
                  </>
                )}
                {r.createdUserId && (
                  <Link
                    className="font-medium text-accent"
                    to={`/admin/doctors/${r.createdUserId}`}
                  >
                    Открыть пользователя
                  </Link>
                )}
                {r.status === 'closed' && !r.createdUserId && (
                  <span className="text-muted">Закрыта без пользователя</span>
                )}
              </div>
            </div>
          ))}
        </Card>
      )}
      {data && (page > 0 || data.total > 50) && (
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            disabled={page === 0}
            onClick={() => setParams({ status, page: String(page - 1) })}
          >
            Назад
          </Button>
          <span className="text-sm text-muted">
            Страница {page + 1} · всего {data.total}
          </span>
          <Button
            variant="secondary"
            disabled={(page + 1) * 50 >= data.total}
            onClick={() => setParams({ status, page: String(page + 1) })}
          >
            Далее
          </Button>
        </div>
      )}
    </section>
  );
}
